import { useCallback } from "react";
import { LatLngLike } from "../types/mapData";

export function useMapCentering(
  map: google.maps.Map | null,
  setViewCenter: (center: LatLngLike) => void,
  showSearchBox: boolean,
  showSidebar: boolean,
  showZoombox: boolean,
  showPassPopup: boolean,
) {
  const handleCenterSetting = useCallback(
    (
      mapProj: google.maps.Projection,
      latlng: LatLngLike,
      offset?: "negative" | "offset",
    ) => {
      const infoContainer = document?.getElementsByClassName(
        "info-container",
      )[0] as HTMLElement;
      let offsetx =
        window.innerWidth >= 800 ? infoContainer?.offsetWidth / 2 + 12 : 0;
      let offsety = window.innerWidth >= 800 ? 0 : 102;
      if (!showSearchBox && !showSidebar && !showZoombox && !showPassPopup) {
        offsetx = 0;
        offsety = 0;
      }

      const isInstance = latlng instanceof google.maps.LatLng;
      const fallbackCenter = map?.getCenter();
      const point1 = mapProj?.fromLatLngToPoint(
        isInstance ? latlng : (fallbackCenter ?? new google.maps.LatLng(0, 0)),
      );
      if (offset === "negative") {
        offsetx = offsetx * -1;
        offsety = offsety * -1;
      } else if (offset === "offset") {
        offsetx = offsetx * 0.5;
        offsety = offsety * -0.5;
      }
      const point2 = new google.maps.Point(
        (typeof offsetx == "number" ? offsetx : 0) /
          Math.pow(2, map?.getZoom() ?? 0),
        (typeof offsety == "number" ? offsety : 0) /
          Math.pow(2, map?.getZoom() ?? 0),
      );
      const center = mapProj?.fromPointToLatLng(
        new google.maps.Point(point1.x - point2.x, point1.y + point2.y),
      );
      map?.panTo(center);
    },
    [map],
  );

  const setCenter = useCallback(
    (latlng: LatLngLike | null, offset?: "negative" | "offset") => {
      if (!latlng) return;
      setViewCenter(latlng);
      const useMap = map;
      if (!useMap) return;
      let mapProj = useMap.getProjection();

      if (!mapProj) {
        google.maps.event.addListenerOnce(useMap, "projection_changed", () => {
          mapProj = useMap.getProjection();
          if (mapProj) {
            handleCenterSetting(mapProj, latlng, offset);
          }
        });
      } else {
        handleCenterSetting(mapProj, latlng, offset);
      }
    },
    [map, setViewCenter, handleCenterSetting],
  );

  const changeCenterOnResize = useCallback(
    ({ latlng, type }: { latlng: LatLngLike; type: "smaller" | "larger" }) => {
      if (!map) return;
      const offset = type === "smaller" ? "negative" : "offset";
      setCenter(latlng, offset);
    },
    [map, setCenter],
  );

  return {
    setCenter,
    changeCenterOnResize,
  };
}
